import { getCopy } from '../../utils/copy';
import { useEffect, useRef, useState } from 'react';

/**
 * 图解数学 · 猫捉老鼠(追及问题)
 *
 * 一条带数字格子的路,老鼠在前、猫在后,每一轮两个同时往前跳。
 * 孩子自己点「一起跳一次」,看猫一步步追近:
 *
 *   每跳一次,两者的距离缩短 (猫步长 − 老鼠步长) 格
 *   → 需要跳 相差格数 ÷ 每次追近格数 次
 *   → 抓到的格子 = 猫的起点 + 猫步长 × 次数
 *
 * 相差格数不能被"每次追近"整除时,猫会在最后一跳直接跳过老鼠,永远落不到
 * 同一格 —— 这是追及题常考的陷阱,画面上也要讲清楚,不当成非法输入。
 *
 * 路是一整行、不折行(折行后从 9 跳到 10 看起来像往回跳,数轴就断了):
 * 画面像镜头一样对准猫和老鼠的中点,每跳一次平滑往前移,前方的格子不断冒出来。
 * 格子宽度按开局距离自动缩放,保证开局时两个都在画面里。
 * 规划见 docs/plan-002-tujie-math-liuyi.md。
 */

const MAX_JUMPS = 20; // 超过这么多次,点按钮演示就没意义了

/**
 * 算猫捉老鼠。
 * 返回 { error } 表示这组数字讲不了;否则返回
 * { gap, closing, jumps, caught, meetAt }
 *   caught=true  : 第 jumps 次正好落在同一格 meetAt
 *   caught=false : 第 jumps 次猫跳过了老鼠
 */
export function solveCatMouse({ catStart, catStep, mouseStart, mouseStep }) {
  const nums = [catStart, catStep, mouseStart, mouseStep];
  if (!nums.every(Number.isInteger) || catStart < 0 || mouseStart < 0) {
    return { error: '起点和步长要是整数,起点不能是负数' };
  }
  if (catStep <= 0 || mouseStep <= 0) return { error: '每次至少跳 1 格' };
  const gap = mouseStart - catStart;
  if (gap <= 0) return { error: '老鼠要在猫的前面' };
  const closing = catStep - mouseStep;
  if (closing <= 0) return { error: '猫每次跳得不比老鼠远,永远追不上' };

  const caught = gap % closing === 0;
  const jumps = Math.ceil(gap / closing);
  if (jumps > MAX_JUMPS) return { error: `要跳 ${jumps} 次,太远了,换小一点的数字` };

  return {
    gap,
    closing,
    jumps,
    caught,
    meetAt: caught ? catStart + catStep * jumps : null,
  };
}

const VIEW_W = 300; // 可视窗口宽度(px)
const TOKEN_H = 64; // 格子上方的空间:猫和老鼠站在底部,往上跳要有地方
const TOKEN_SIZE = 28;
const HOP_PX = 26;
const CELL_H = 30;

/** 格子宽度:开局两者之间的距离加两侧留白要放进窗口(镜头会跟着走,不用给猫的第一步留位置),但别小到两位数挤在一起 */
function cellWidthFor(gap) {
  return Math.max(20, Math.min(34, Math.floor(VIEW_W / (gap + 3))));
}

function Token({ pos, emoji, testId, offset, cell, hop }) {
  const ref = useRef(null);
  // 每跳一次往上弹一下再落下;jsdom 没有 animate(),测试里直接跳过
  useEffect(() => {
    if (hop > 0) {
      ref.current?.animate?.(
        [
          { transform: 'translateY(0)' },
          { transform: `translateY(-${HOP_PX}px)` },
          { transform: 'translateY(0)' },
        ],
        { duration: 500, easing: 'ease-out' },
      );
    }
  }, [hop]);

  return (
    <span
      data-testid={testId}
      data-pos={pos}
      className="absolute flex items-center justify-center text-2xl leading-none transition-[left] duration-500 ease-out"
      style={{ left: pos * cell + offset, width: cell, height: TOKEN_SIZE, top: TOKEN_H - TOKEN_SIZE - 2 }}
    >
      <span ref={ref}>{emoji}</span>
    </span>
  );
}

function CatMouseExplainer({ catStart, catStep, mouseStart, mouseStep, onDone, ageBand }) {
  const [t, setT] = useState(0);
  const result = solveCatMouse({ catStart, catStep, mouseStart, mouseStep });

  if (result.error) {
    return <p className="text-center text-base text-gray-600">这组数字讲不了:{result.error}。</p>;
  }

  const { gap, closing, jumps, caught, meetAt } = result;
  const catAt = (k) => catStart + catStep * k;
  const mouseAt = (k) => mouseStart + mouseStep * k;
  const done = t === jumps;

  const cell = cellWidthFor(gap);
  const viewCells = Math.ceil(VIEW_W / cell);
  // 镜头对准两者中点;路永远画到镜头右边缘再往前一截,看起来无限延伸
  const mid = (catAt(t) + mouseAt(t)) / 2;
  const camera = Math.max(0, (mid + 0.5) * cell - VIEW_W / 2);
  const lastCell = Math.ceil((camera + VIEW_W) / cell) + viewCells;
  const cells = Array.from({ length: lastCell + 1 }, (_, i) => i);


  const together = catAt(t) === mouseAt(t);
  const gapNow = mouseAt(t) - catAt(t);

  // 每跳一次还差几格,缩成一排小标签
  const gapChips = Array.from({ length: t + 1 }, (_, k) => {
    const g = mouseAt(k) - catAt(k);
    if (g === 0) return '抓到!';
    if (g < 0) return '跳过头';
    return `差 ${g} 格`;
  });

  let explanation;
  if (t === 0) {
    explanation = (
      <>
        老鼠🐭在 <b>{mouseStart}</b>,每次跳 <b>{mouseStep}</b> 格;猫🐱在 <b>{catStart}</b>,每次跳{' '}
        <b>{catStep}</b> 格。它们一起跳,猫能在哪一格抓到老鼠?
      </>
    );
  } else if (!done) {
    explanation = (
      <>
        猫跳到 {catAt(t)},老鼠跳到 {mouseAt(t)},还差 <b>{gapNow}</b> 格。每跳一次,猫追近{' '}
        {catStep} − {mouseStep} = <b>{closing}</b> 格。
      </>
    );
  } else if (caught) {
    explanation = (
      <>
        抓到啦!在第 <b>{meetAt}</b> 格。一开始差 {gap} 格,每次追近 {closing} 格:{gap} ÷ {closing} ={' '}
        <b>{jumps}</b> 次;猫跳到 {catStart} + {catStep} × {jumps} = <b>{meetAt}</b>。
      </>
    );
  } else {
    explanation = (
      <>
        哎呀,猫<b>跳过头</b>了!只差 {mouseAt(t - 1) - catAt(t - 1)} 格的时候,猫一次追近 {closing}{' '}
        格,直接跳到老鼠前面去了。{gap} 格不能被 {closing} 整除,所以猫<b>永远落不到</b>老鼠的格子上。
      </>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <div
        className="relative overflow-hidden rounded-lg bg-green-100"
        style={{ width: VIEW_W, height: TOKEN_H + CELL_H + 6 }}
      >
        <div
          data-testid="road"
          className="absolute inset-y-0 left-0 transition-transform duration-500 ease-out"
          style={{ transform: `translateX(${-camera}px)` }}
        >
          {cells.map((pos) => {
            const hit = done && caught && pos === meetAt;
            return (
              <div
                key={pos}
                data-testid={`cell-${pos}`}
                className={`absolute flex flex-col items-center justify-start border-y-2 border-r-2 border-gray-800 font-bold
                            ${pos === 0 ? 'border-l-2' : ''}
                            ${hit ? 'bg-qiji-coral text-white' : 'bg-white text-gray-800'}`}
                style={{
                  left: pos * cell,
                  top: TOKEN_H,
                  width: cell,
                  height: CELL_H,
                  fontSize: cell < 24 ? 10 : 12,
                }}
              >
                {pos}
                <div className="flex gap-0.5">
                  {/* 只标猫和老鼠当前站的格子;走过的不留脚印,不然一长串点看着乱 */}
                  {pos === catAt(t) && <span className="h-1.5 w-1.5 rounded-full bg-orange-500" />}
                  {pos === mouseAt(t) && <span className="h-1.5 w-1.5 rounded-full bg-gray-500" />}
                </div>
              </div>
            );
          })}
          <Token
            pos={mouseAt(t)}
            emoji="🐭"
            testId="mouse"
            offset={together ? 5 : 0}
            cell={cell}
            hop={t}
          />
          <Token
            pos={catAt(t)}
            emoji={together ? '😼' : '🐱'}
            testId="cat"
            offset={together ? -5 : 0}
            cell={cell}
            hop={t}
          />
        </div>
      </div>

      <div className="flex min-h-[1.75rem] flex-wrap justify-center gap-1.5">
        {gapChips.map((chip, k) => (
          <span
            key={k}
            data-testid="gap-chip"
            className="rounded-full border border-gray-400 bg-white px-2.5 py-0.5 text-sm text-gray-700"
          >
            {chip}
          </span>
        ))}
      </div>

      <p
        data-testid="explanation"
        className="min-h-[4.5rem] w-full rounded-lg bg-qiji-gold px-3 py-2 text-base leading-relaxed text-gray-900"
      >
        {explanation}
      </p>

      <div className="flex flex-wrap items-center justify-center gap-3">
        {!done ? (
          <>
            <button
              type="button"
              onClick={() => setT(t + 1)}
              className="rounded-lg bg-qiji-mint px-5 py-2.5 text-base font-bold text-gray-900
                         border-2 border-black shadow-brutal-sm transition
                         hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
            >
              一起跳一次!
            </button>
            {/* 自己点过两次、看明白规律后,允许快进 */}
            {t >= 2 && (
              <button
                type="button"
                onClick={() => setT(jumps)}
                className="rounded-lg bg-white px-4 py-2.5 text-base font-bold text-gray-900
                           border-2 border-black shadow-brutal-sm transition
                           hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
              >
                一口气跳到底
              </button>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setT(0)}
              className="rounded-lg bg-white px-4 py-2.5 text-base font-bold text-gray-900
                         border-2 border-black shadow-brutal-sm transition
                         hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
            >
              再看一遍
            </button>
            {onDone && (
              <button
                type="button"
                onClick={onDone}
                className="rounded-lg bg-qiji-gold px-5 py-2.5 text-base font-bold text-gray-900
                           border-2 border-black shadow-brutal-sm transition
                           hover:-translate-y-0.5 active:translate-y-0 active:shadow-none"
              >
                {getCopy(ageBand).explainerButton}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default CatMouseExplainer;
